import { api, type UploadResponse } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import { compressImage, type CompressPreset } from './compress';

export const ACCEPTED_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
] as const;

export type AcceptedMime = (typeof ACCEPTED_MIME)[number];

export interface UploadImageOptions {
  /** Skip compression entirely. Default: compress. */
  compress?: boolean;
  /** Preset for the compressor. Default: 'photo'. */
  preset?: CompressPreset;
  /** Max file size in MB pre-compression (validation). Default 10. */
  maxSizeMB?: number;
  /** Progress callback (0–100). */
  onProgress?: (pct: number) => void;
  /** AbortSignal to cancel the upload. */
  signal?: AbortSignal;
  /** Number of retry attempts on network errors. Default 2. */
  retries?: number;
}

export interface UploadImageResult {
  url: string;
  filename: string;
  /** Final size in bytes (after compression, if any). Useful for analytics. */
  bytes: number;
}

/**
 * Validate, compress, and upload an image.
 *
 * Returns a **host-relative** `/uploads/<file>` path — never an absolute URL.
 * Baking the host in (the old behaviour) leaked `localhost` into persisted
 * entity data and broke in production. Render sites resolve the path to the
 * live host with `resolveImageUrl()`.
 */
export async function uploadImage(
  file: File,
  opts: UploadImageOptions = {},
): Promise<UploadImageResult> {
  validate(file, opts.maxSizeMB ?? 10);

  const prepared =
    opts.compress === false ? file : await compressImage(file, opts.preset ?? 'photo');

  const retries = opts.retries ?? 2;
  let attempt = 0;
  let lastErr: unknown;

  while (attempt <= retries) {
    if (opts.signal?.aborted) {
      throw new ApiError({
        message: 'Upload aborted',
        status: 0,
        code: 'CANCELED',
      });
    }
    try {
      const res: UploadResponse = await api.upload(prepared, {
        onProgress: opts.onProgress,
        signal: opts.signal,
      });
      // Persist a host-relative path; the host is resolved at render time.
      return {
        url: `/uploads/${res.filename}`,
        filename: res.filename,
        bytes: prepared.size,
      };
    } catch (err) {
      lastErr = err;
      const code = err instanceof ApiError ? err.code : undefined;
      // Don't retry on cancel/validation/auth — only transient network/server.
      if (code === 'CANCELED' || code === 'VALIDATION' || code === 'UNAUTHORIZED') {
        throw err;
      }
      attempt += 1;
      if (attempt > retries) break;
      await sleep(300 * attempt);
    }
  }

  throw lastErr;
}

function validate(file: File, maxSizeMB: number): void {
  if (!(ACCEPTED_MIME as readonly string[]).includes(file.type)) {
    throw new ApiError({
      message: `Unsupported file type: ${file.type || 'unknown'}`,
      status: 0,
      code: 'VALIDATION',
    });
  }
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new ApiError({
      message: `File too large (max ${maxSizeMB} MB)`,
      status: 0,
      code: 'VALIDATION',
    });
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
